import React from "react";

// Walkthrough shown on the in-app "Getting Started" nav page. There are two versions, chosen by
// role: one for vehicle owners sending requests, one for shops. Keep these steps in sync with the
// real site flows - see the "Keep the Getting Started page in sync" rule in CLAUDE.md / AGENTS.md.

const OWNER_INTRO =
  "Here is how to go from a car problem to the right shop. You can come back to this page any time.";

const OWNER_STEPS = [
  {
    title: "Describe your vehicle and the issue",
    body:
      "On Car Request, pick your vehicle's make, model, and year, then describe what is happening. The more specific you are about noises, warning lights, leaks, or what you want done, the faster shops can help.",
    action: { label: "Car Request", view: "home" },
  },
  {
    title: "Find nearby shops",
    body:
      "Enter your ZIP code - or tap Use my location to fill it in - and search. If nothing comes up, expand the search radius, and filter for make specialists or general repair shops to match your needs.",
  },
  {
    title: "Send one request to several shops",
    body:
      "Select a few good-fit shops and send a single request to all of them at once. The request coach helps you add the details shops need before you send.",
  },
  {
    title: "Compare replies in Messages",
    body:
      "Shops respond in your inbox. Compare their guidance, quotes, and response times side by side so you can choose with confidence.",
    action: { label: "Messages", view: "messages" },
  },
  {
    title: "Choose the best shop",
    body:
      "Keep chatting with the shop you like, favorite shops to find them again later, and start a work order when you are ready to book.",
  },
  {
    title: "Stay reachable",
    body:
      "Keep your profile up to date and turn on message notifications so you never miss a reply from a shop.",
    action: { label: "Profile", view: "profile" },
  },
];

const SHOP_INTRO =
  "Here is how to set up your shop so owners can find you, then turn new leads into work orders. You can come back to this page any time.";

const SHOP_STEPS = [
  {
    title: "Complete your shop profile",
    body:
      "Confirm your shop name and location so owners know who you are and where you serve. This is the foundation of the shop card owners see.",
    action: { label: "Profile", view: "profile" },
  },
  {
    title: "Add your logo and business links",
    body:
      "Make your shop card recognizable and give owners ways to learn more about your business. Add links to your website, social pages, and Google Business Profile so owners can open your Google listing and reviews.",
  },
  {
    title: "Add make specializations",
    body:
      "Highlight the makes your shop knows best so you are matched to the requests you are a good fit for.",
  },
  {
    title: "Add services offered",
    body:
      "Select the services your shop provides, like brakes, oil changes, or transmission repair, so owners can see what you handle at a glance.",
  },
  {
    title: "Confirm general repair coverage",
    body:
      "Let owners know whether you also handle general repair requests, not just the makes you specialize in.",
  },
  {
    title: "Turn on message notifications",
    body:
      "Keep email or browser notifications on so new leads are not missed and you can respond quickly.",
  },
  {
    title: "Preview your shop card",
    body:
      "Check the exact card owners see in shop search and requests, so you know how you come across.",
    action: { label: "Shop Preview", view: "shopPreview" },
  },
  {
    title: "Respond to requests in Messages",
    body:
      "Reply to new leads in your inbox, answer questions, send quotes, and start a work order when an owner is ready.",
    action: { label: "Messages", view: "messages" },
  },
  {
    title: "See how you appear to owners",
    body:
      "Use Shop Explorer to browse the way owners do and see your shop alongside others in your area.",
    action: { label: "Shop Explorer", view: "shopExplorer" },
  },
];

function GettingStartedPage({ isShopUser = false, onNavigate }) {
  const steps = isShopUser ? SHOP_STEPS : OWNER_STEPS;
  const intro = isShopUser ? SHOP_INTRO : OWNER_INTRO;
  const heading = isShopUser
    ? "Getting started with your shop"
    : "Getting started with GearCheckAuto";

  return (
    <div className="max-w-3xl mx-auto space-y-3">
      <div className="rounded-2xl border border-blue-200 dark:border-blue-900 bg-blue-50/80 dark:bg-blue-950/40 p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
          Getting started
        </p>
        <h1 className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{heading}</h1>
        <p className="mt-2 text-sm text-blue-950 dark:text-blue-200 leading-relaxed">{intro}</p>
      </div>

      <ol className="space-y-3">
        {steps.map((step, index) => (
          <li key={step.title} className="border rounded-xl bg-white dark:bg-gray-800 shadow-sm p-4">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                {index + 1}
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{step.title}</h2>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{step.body}</p>
                {step.action && onNavigate ? (
                  <button
                    type="button"
                    onClick={() => onNavigate(step.action.view)}
                    className="mt-3 inline-flex items-center gap-1 rounded-xl bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Go to {step.action.label}
                  </button>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default GettingStartedPage;
